import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import { DinoActor, type FaunaSense } from '../../../dinos/DinoActor';
import { BARYONYX_KIT, GALLI_KIT, SPINO_KIT, TRIKE_KIT } from '../../../dinos/kits';
import { nearestInteract, queryInteract, type InteractTarget, type Xyz } from '../../../interact/Interact';
import type { Grid } from '../../../inventory/Grid';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';
import { setPrompt } from '../../../ui/Prompt';
import { areaAt } from '../a02/DeepJungle';
import { SB_AREA_X } from '../a04/SwampOfBones';
import { VZ_AREA_Z } from '../a05/VolcanicZone';
import { inUnderground } from '../a06/Underground';
import { inLostWorld } from '../a07/LostWorld';

export type RiverlandsPoint = { x: number; y: number; z: number };
export type WorldArea =
  | 'crash_coast'
  | 'deep_jungle'
  | 'riverlands'
  | 'swamp'
  | 'volcanic'
  | 'underground'
  | 'lost_world';

export type RiverlandsFlags = {
  boatReady: boolean;
};

export type RiverlandsHook = {
  area?: WorldArea;
  prompt?: string;
  flags: { boatReady?: boolean };
  dinos?: { id: string; x: number; y: number; z: number; hp: number; kind: string; taxon: string }[];
};

/** Dressing streams in once the player is east of the Crash Coast cliffs. */
export const RL_STREAM_X = 40;
/** Area slug flips past the cliff fringe, not on the face. */
export const RL_AREA_X = 50;

export const RL_DOCK: RiverlandsPoint = { x: 80, y: 1, z: -10 };

/** Stub cost. Full zodiac later wants wood >= 20. */
export const RL_PATCH_WOOD = 8;
export const RL_PATCH_FIBER = 4;

export const RL_NODES: Record<string, RiverlandsPoint> = {
  'RL-DOCK': RL_DOCK,
  'RL-ZODIAC': { x: 80, y: 0.38, z: -7.55 },
  'RL-FALL': { x: 46.2, y: 4.2, z: -2.4 },
  'RL-BARYONYX': { x: 72, y: 0.35, z: -6 },
  'RL-GALLI': { x: 64, y: 1, z: 10 },
  'RL-TRIKE': { x: 92, y: 1, z: -28 },
  'RL-SPINO': { x: 106, y: 0.4, z: 12 },
};

const GROUND_HALF_Y = 0.5;
const HUNTER_BOUNDS = { minX: 58, maxX: 98, minZ: -16, maxZ: 2 };
const GALLI_BOUNDS = { minX: 52, maxX: 78, minZ: 4, maxZ: 16 };
const TRIKE_BOUNDS = { minX: 82, maxX: 104, minZ: -36, maxZ: -18 };
const SPINO_BOUNDS = { minX: 100, maxX: 112, minZ: 4, maxZ: 16 };
const _look = new THREE.Vector3();

type RiverMats = {
  bank: THREE.MeshStandardMaterial;
  wetSand: THREE.MeshStandardMaterial;
  water: THREE.MeshPhysicalMaterial;
  foam: THREE.MeshStandardMaterial;
  plank: THREE.MeshStandardMaterial;
  piling: THREE.MeshStandardMaterial;
  reed: THREE.MeshStandardMaterial;
  rock: THREE.MeshStandardMaterial;
  station: THREE.MeshStandardMaterial;
  roof: THREE.MeshStandardMaterial;
  rust: THREE.MeshStandardMaterial;
  hull: THREE.MeshStandardMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): RiverMats {
  return {
    bank: new THREE.MeshStandardMaterial({
      color: 0x8a7a58,
      map: loadMap('/tex/sand.jpg', 6, 8),
      roughness: 0.96,
      metalness: 0.02,
    }),
    wetSand: new THREE.MeshStandardMaterial({
      color: 0x6e5a40,
      map: loadMap('/tex/sand.jpg', 5, 3),
      roughness: 0.58,
      metalness: 0.05,
    }),
    reed: new THREE.MeshStandardMaterial({
      color: 0x4a6a3a,
      map: loadMap('/tex/fern.jpg', 1, 2),
      roughness: 0.84,
      metalness: 0.02,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.3,
    }),
    rock: new THREE.MeshStandardMaterial({
      color: 0x6a6458,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.94,
      metalness: 0.05,
    }),
    water: new THREE.MeshPhysicalMaterial({
      color: 0x1c6a62,
      roughness: 0.2,
      metalness: 0.06,
      transparent: true,
      opacity: 0.72,
      clearcoat: 0.28,
      clearcoatRoughness: 0.4,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    foam: new THREE.MeshStandardMaterial({
      color: 0xd4e4de,
      roughness: 0.5,
      metalness: 0.02,
      transparent: true,
      opacity: 0.58,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    plank: new THREE.MeshStandardMaterial({
      color: 0x6a5a42,
      map: loadMap('/tex/bark.jpg', 2, 1),
      roughness: 0.9,
      metalness: 0.04,
    }),
    piling: new THREE.MeshStandardMaterial({
      color: 0x4a4034,
      roughness: 0.92,
      metalness: 0.06,
    }),
    station: new THREE.MeshStandardMaterial({
      color: 0x7a7872,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.9,
      metalness: 0.08,
    }),
    roof: new THREE.MeshStandardMaterial({
      color: 0x3a3834,
      roughness: 0.86,
      metalness: 0.12,
    }),
    rust: new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      map: loadMap('/tex/wreck.jpg', 1, 1),
      roughness: 0.7,
      metalness: 0.28,
    }),
    hull: new THREE.MeshStandardMaterial({
      color: 0x4a4e48,
      roughness: 0.55,
      metalness: 0.12,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    }),
  };
}

export type AreaFlags = { keyA?: boolean; keyB?: boolean; keyC?: boolean };

/** Shaft below, gated vale north, river east, volcanic, swamp west; inland jungle uses z. */
export function resolveArea(x: number, z: number, y = 0, flags?: AreaFlags): WorldArea {
  if (inUnderground(y)) {
    return 'underground';
  }
  if (x > RL_AREA_X) {
    return 'riverlands';
  }
  if (inLostWorld(z, y, flags)) {
    return 'lost_world';
  }
  if (z < VZ_AREA_Z) {
    return 'volcanic';
  }
  if (x < SB_AREA_X) {
    return 'swamp';
  }
  return areaAt(z);
}

export function canPatchZodiac(
  inv: { countOf(id: string): number },
  ready = false,
): boolean {
  return !ready && inv.countOf('wood') >= RL_PATCH_WOOD && inv.countOf('fiber') >= RL_PATCH_FIBER;
}

/**
 * Glassvein fringe east of Crash Coast. Coast / jungle stay resident;
 * this dressing streams in at x > 40 and the area slug flips at x > 50.
 */
export class Riverlands {
  streamed = false;
  prompt = '';
  hunter: DinoActor | null = null;
  readonly extras: DinoActor[] = [];
  readonly flags: RiverlandsFlags = { boatReady: false };
  private groundReady = false;
  private boat: InteractTarget | null = null;
  private hull: THREE.Mesh | null = null;

  constructor(private readonly inventory: Grid) {}

  extendGround(physics: PhysicsWorld): void {
    if (this.groundReady) {
      return;
    }
    // Default slab ends at x ≈ 32. Cover the cliff fringe through Station Current.
    physics.addStaticCuboid(40, GROUND_HALF_Y, 26, 72, -GROUND_HALF_Y, -10);
    this.groundReady = true;
  }

  /** Load dressing the first time the player is east of the cliffs. */
  sync(scene: THREE.Scene, physics: PhysicsWorld, x: number): void {
    this.extendGround(physics);
    if (this.streamed || x <= RL_STREAM_X) {
      return;
    }
    this.build(scene, physics);
  }

  tick(dt: number, camera: THREE.Camera, player: Xyz, use: boolean, sense?: FaunaSense): boolean {
    if (!this.streamed) {
      return false;
    }
    this.hunter?.tick(dt, player, sense);
    for (const dino of this.extras) {
      dino.tick(dt, player, sense);
    }
    const hit = this.query(camera, player);
    this.applyPrompt(hit);
    if (!use) {
      return hit !== null;
    }
    return this.use(hit);
  }

  forceUse(camera: THREE.Camera, player: Xyz): boolean {
    if (!this.streamed) {
      return false;
    }
    const hit = this.query(camera, player) ?? this.nearest(player);
    const ok = this.use(hit);
    this.applyPrompt(this.query(camera, player) ?? this.nearest(player));
    return ok;
  }

  syncPrompt(camera: THREE.Camera, player: Xyz): void {
    if (!this.streamed) {
      return;
    }
    this.applyPrompt(this.query(camera, player));
  }

  restore(flags: { boatReady?: unknown }): void {
    this.flags.boatReady = flags.boatReady === true;
    this.applyBoat();
  }

  /** QA: `__DS.give('boatReady')` stamps the patched-zodiac flag. */
  grantBoat(ready = true): void {
    this.flags.boatReady = ready;
    this.applyBoat();
  }

  publish(hook: RiverlandsHook): void {
    hook.flags.boatReady = this.flags.boatReady;
    const extra = this.extras.map((dino) => dino.snap());
    if (this.hunter || extra.length > 0) {
      hook.dinos = [...(hook.dinos ?? []), ...(this.hunter ? [this.hunter.snap()] : []), ...extra];
    }
    if (this.prompt) {
      hook.prompt = this.prompt;
    }
  }

  private query(camera: THREE.Camera, player: Xyz) {
    camera.getWorldDirection(_look);
    return queryInteract(player, _look, player, this.targets());
  }

  private nearest(player: Xyz) {
    return nearestInteract(player, this.targets());
  }

  private targets(): InteractTarget[] {
    return this.boat && this.boat.live ? [this.boat] : [];
  }

  private use(hit: { target: InteractTarget } | null): boolean {
    if (hit?.target.id !== 'RL-ZODIAC' || !this.boat?.live) {
      return false;
    }
    if (!canPatchZodiac(this.inventory, this.flags.boatReady)) {
      return false;
    }
    if (!this.inventory.remove('wood', RL_PATCH_WOOD) || !this.inventory.remove('fiber', RL_PATCH_FIBER)) {
      return false;
    }
    this.flags.boatReady = true;
    this.applyBoat();
    return true;
  }

  private applyPrompt(hit: { target: InteractTarget } | null): void {
    this.prompt = hit?.target.prompt ?? '';
    if (this.prompt) {
      setPrompt(this.prompt);
    }
  }

  private applyBoat(): void {
    if (!this.flags.boatReady) {
      return;
    }
    if (this.boat) {
      this.boat.live = false;
    }
    const mat = this.hull?.material;
    if (mat && !Array.isArray(mat)) {
      mat.opacity = 0.92;
      mat.transparent = false;
      mat.depthWrite = true;
    }
  }

  private build(scene: THREE.Scene, physics: PhysicsWorld): void {
    this.streamed = true;
    const root = new THREE.Group();
    tag(root, 'Riverlands');
    root.userData.area = 'riverlands';
    scene.add(root);

    const mats = makeMats();
    addBank(root, mats);
    addRiver(root, mats);
    addWetSand(root, mats);
    addReeds(root, mats);
    addBankRocks(root, physics, mats);
    addDriftwood(root, mats);
    addWaterfall(root, mats);
    addDock(root, physics, mats);
    this.hull = addZodiac(root, mats);
    this.boat = {
      id: 'RL-ZODIAC',
      prompt: 'Patch Zodiac',
      position: { ...(RL_NODES['RL-ZODIAC'] ?? { x: 80, y: 0.38, z: -7.55 }) },
      radius: 1.4,
      live: !this.flags.boatReady,
    };
    this.spawnHunter(root);
    this.applyBoat();
  }

  private spawnHunter(root: THREE.Group): void {
    const spot = RL_NODES['RL-BARYONYX'] ?? { x: 72, y: 0.35, z: -6 };
    this.hunter = new DinoActor('RL-BARYONYX', BARYONYX_KIT, spot.x, spot.z, HUNTER_BOUNDS);
    root.add(this.hunter.mesh);

    const galliSpot = RL_NODES['RL-GALLI'] ?? { x: 64, y: 1, z: 10 };
    const galli = new DinoActor('RL-GALLI', GALLI_KIT, galliSpot.x, galliSpot.z, GALLI_BOUNDS);
    root.add(galli.mesh);
    this.extras.push(galli);

    const trikeSpot = RL_NODES['RL-TRIKE'] ?? { x: 92, y: 1, z: -28 };
    const trike = new DinoActor('RL-TRIKE', TRIKE_KIT, trikeSpot.x, trikeSpot.z, TRIKE_BOUNDS);
    root.add(trike.mesh);
    this.extras.push(trike);

    const spinoSpot = RL_NODES['RL-SPINO'] ?? { x: 106, y: 0.4, z: 12 };
    const spino = new DinoActor('RL-SPINO', SPINO_KIT, spinoSpot.x, spinoSpot.z, SPINO_BOUNDS);
    root.add(spino.mesh);
    this.extras.push(spino);
  }
}

function addBank(root: THREE.Group, mats: RiverMats): void {
  const geo = new THREE.PlaneGeometry(82, 54, 20, 14);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) + 74;
      const z = pos.getZ(i) - 8;
      const riverZ = riverCenterZ(x);
      const dist = Math.abs(z - riverZ);
      let y = Math.sin(x * 0.08 + z * 0.12) * 0.05;
      if (dist < 6.2) {
        y = -0.02;
      } else if (dist < 9.5) {
        y = 0.12 + (dist - 6.2) * 0.04;
      }
      if (Math.hypot(x - 70, z - 0) < 4 || Math.hypot(x - 80, z + 10) < 5) {
        y = 0;
      }
      pos.setY(i, 0.016 + y);
    }
    pos.needsUpdate = true;
  }
  geo.computeVertexNormals();
  const floor = new THREE.Mesh(geo, mats.bank);
  floor.position.set(74, 0, -8);
  floor.receiveShadow = true;
  tag(floor, 'RL-BANK');
  root.add(floor);

  const berms: [number, number, number, number][] = [
    [62, 6.5, 10, 3.2],
    [78, 8.2, 12, 2.8],
    [94, 7.4, 11, 3.0],
    [58, -16, 9, 2.6],
    [86, -18.5, 11, 2.9],
    [102, -14, 10, 2.7],
  ];
  for (let i = 0; i < berms.length; i++) {
    const b = berms[i];
    if (!b) {
      continue;
    }
    const [x, z, sx, sz] = b;
    const berm = new THREE.Mesh(new THREE.PlaneGeometry(sx, sz), mats.bank);
    berm.rotation.x = -Math.PI / 2;
    berm.position.set(x, 0.11, z);
    berm.receiveShadow = true;
    tag(berm, `RL-BERM-${String(i + 1).padStart(2, '0')}`);
    root.add(berm);
  }
}

function addRiver(root: THREE.Group, mats: RiverMats): void {
  const curve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(42, 0.07, 4),
    new THREE.Vector3(54, 0.07, -2),
    new THREE.Vector3(68, 0.07, -7),
    new THREE.Vector3(82, 0.07, -8.5),
    new THREE.Vector3(96, 0.07, -6),
    new THREE.Vector3(110, 0.07, 2),
  ]);
  const river = new THREE.Mesh(new THREE.TubeGeometry(curve, 36, 5.2, 10, false), mats.water);
  river.receiveShadow = false;
  river.renderOrder = 1;
  tag(river, 'RL-RIVER');
  root.add(river);

  const lobes: [number, number, number, number][] = [
    [50, 1.2, 14, 8.4],
    [68, -6.4, 16, 9.2],
    [82, -8.2, 15, 8.8],
    [98, -5.2, 14, 8.6],
  ];
  for (let i = 0; i < lobes.length; i++) {
    const l = lobes[i];
    if (!l) {
      continue;
    }
    const [x, z, sx, sz] = l;
    const lobe = new THREE.Mesh(new THREE.CircleGeometry(Math.max(sx, sz) * 0.42, 16), mats.water);
    lobe.rotation.x = -Math.PI / 2;
    lobe.scale.set(sx / 10, 1, sz / 8);
    lobe.position.set(x, 0.085, z);
    lobe.renderOrder = 1;
    tag(lobe, i === 1 ? 'RL-RIVER-SHEET' : `RL-RIVER-LOBE-${String(i + 1).padStart(2, '0')}`);
    root.add(lobe);
  }
}

function addWetSand(root: THREE.Group, mats: RiverMats): void {
  const strips: [number, number, number, number, number][] = [
    [52, 5.6, 12, 2.4, 0.35],
    [68, -1.2, 14, 2.2, 0.2],
    [84, -2.6, 13, 2.1, 0.15],
    [98, 0.4, 12, 2.3, -0.2],
    [56, -10.4, 11, 2.0, 0.25],
    [76, -14.2, 13, 2.2, 0.1],
    [94, -12.6, 12, 2.1, -0.15],
  ];
  for (let i = 0; i < strips.length; i++) {
    const s = strips[i];
    if (!s) {
      continue;
    }
    const [x, z, sx, sz, yaw] = s;
    const wet = new THREE.Mesh(new THREE.PlaneGeometry(sx, sz), mats.wetSand);
    wet.rotation.x = -Math.PI / 2;
    wet.rotation.z = yaw;
    wet.position.set(x, 0.05, z);
    wet.receiveShadow = true;
    tag(wet, `RL-WET-${String(i + 1).padStart(2, '0')}`);
    root.add(wet);
  }
}

function addReeds(root: THREE.Group, mats: RiverMats): void {
  const clumps: [number, number][] = [
    [48, 8.2],
    [58, 6.4],
    [70, 4.8],
    [88, 3.2],
    [102, 6.0],
    [50, -12.5],
    [64, -16.2],
    [90, -17.4],
    [104, -12.8],
    [46, -6.0],
    [108, -2.4],
    [60, 9.6],
  ];
  const blade = new THREE.PlaneGeometry(0.32, 1.85);
  for (let i = 0; i < clumps.length; i++) {
    const c = clumps[i];
    if (!c) {
      continue;
    }
    const g = new THREE.Group();
    tag(g, `RL-REED-${String(i + 1).padStart(2, '0')}`);
    g.position.set(c[0], 0.82, c[1]);
    for (let k = 0; k < 6; k++) {
      const card = new THREE.Mesh(blade, mats.reed);
      card.position.set((k - 2.5) * 0.11, 0, (k % 2) * 0.09);
      card.rotation.y = k * 0.5 + i * 0.18;
      g.add(card);
    }
    root.add(g);
  }
}

function addBankRocks(root: THREE.Group, physics: PhysicsWorld, mats: RiverMats): void {
  const specs: [number, number, number][] = [
    [54, 10.4, 0.7],
    [92, 8.6, 0.85],
    [48, -14.8, 0.75],
    [106, -10.2, 0.9],
    [62, 12.2, 0.55],
    [100, 10.8, 0.65],
  ];
  for (let i = 0; i < specs.length; i++) {
    const s = specs[i];
    if (!s) {
      continue;
    }
    const [x, z, r] = s;
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), mats.rock);
    rock.position.set(x, r * 0.42, z);
    rock.rotation.set(0.2, x * 0.1, 0.12);
    rock.castShadow = true;
    rock.receiveShadow = true;
    tag(rock, `RL-ROCK-${String(i + 1).padStart(2, '0')}`);
    root.add(rock);
    physics.addStaticCuboid(r * 0.5, r * 0.38, r * 0.5, x, r * 0.42, z);
  }
}

function addDriftwood(root: THREE.Group, mats: RiverMats): void {
  const logs: [number, number, number][] = [
    [58, -13.6, 0.6],
    [96, 5.2, -0.4],
    [74, 6.8, 0.9],
  ];
  for (let i = 0; i < logs.length; i++) {
    const l = logs[i];
    if (!l) {
      continue;
    }
    const [x, z, yaw] = l;
    const log = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.13, 1.8, 6), mats.plank);
    log.rotation.z = Math.PI / 2;
    log.rotation.y = yaw;
    log.position.set(x, 0.14, z);
    log.castShadow = true;
    log.receiveShadow = true;
    tag(log, `RL-DRIFT-${String(i + 1).padStart(2, '0')}`);
    root.add(log);
  }
}

function riverCenterZ(x: number): number {
  if (x < 54) {
    return 4 + (x - 42) * ((-2 - 4) / 12);
  }
  if (x < 68) {
    return -2 + (x - 54) * ((-7 + 2) / 14);
  }
  if (x < 82) {
    return -7 + (x - 68) * ((-8.5 + 7) / 14);
  }
  if (x < 96) {
    return -8.5 + (x - 82) * ((-6 + 8.5) / 14);
  }
  return -6 + (x - 96) * ((2 + 6) / 14);
}

function addWaterfall(root: THREE.Group, mats: RiverMats): void {
  const pos = RL_NODES['RL-FALL'] ?? { x: 46.2, y: 4.2, z: -2.4 };
  const card = new THREE.Mesh(new THREE.PlaneGeometry(6.4, 8.2), mats.water);
  card.position.set(pos.x, pos.y, pos.z);
  card.rotation.y = Math.PI / 2;
  card.renderOrder = 2;
  tag(card, 'RL-FALL');
  root.add(card);

  const mist = new THREE.Mesh(new THREE.PlaneGeometry(7.2, 2.1), mats.foam);
  mist.position.set(pos.x + 0.4, 0.55, pos.z);
  mist.rotation.x = -Math.PI / 2;
  mist.renderOrder = 3;
  tag(mist, 'RL-FALL-MIST');
  root.add(mist);
}

function addDock(root: THREE.Group, physics: PhysicsWorld, mats: RiverMats): void {
  const dock = new THREE.Group();
  tag(dock, 'Station Current');
  dock.position.set(RL_DOCK.x, 0, RL_DOCK.z);
  root.add(dock);

  const deck = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.22, 3.4), mats.plank);
  deck.position.set(0, 0.22, 0.4);
  deck.castShadow = true;
  deck.receiveShadow = true;
  tag(deck, 'RL-DOCK');
  dock.add(deck);

  const posts: [number, number][] = [
    [-3.2, -1.1],
    [3.2, -1.1],
    [-3.2, 1.8],
    [3.2, 1.8],
  ];
  for (let i = 0; i < posts.length; i++) {
    const p = posts[i];
    if (!p) {
      continue;
    }
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 1.15, 7), mats.piling);
    post.position.set(p[0], 0.35, p[1]);
    post.castShadow = true;
    dock.add(post);
  }

  const hut = new THREE.Mesh(new THREE.BoxGeometry(3.2, 2.2, 2.6), mats.station);
  hut.position.set(-1.4, 1.35, -0.15);
  hut.castShadow = true;
  hut.receiveShadow = true;
  tag(hut, 'RL-STATION');
  dock.add(hut);

  const roof = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.14, 3), mats.roof);
  roof.position.set(-1.4, 2.5, -0.15);
  roof.castShadow = true;
  dock.add(roof);

  const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.22, 0.05), mats.rust);
  plaque.position.set(-1.4, 2.05, 1.18);
  dock.add(plaque);

  physics.addStaticCuboid(3.6, 0.16, 1.7, RL_DOCK.x, 0.22, RL_DOCK.z + 0.4);
  physics.addStaticCuboid(1.6, 1.1, 1.3, RL_DOCK.x - 1.4, 1.35, RL_DOCK.z - 0.15);
  void dressGlassveinDock(dock, mats);
}

const DOCK_GLB = '/models/bld/bld_glassvein_dock.glb';

/** Swap the box dock for the authored Station Current GLB. Keeps primitives if the file is missing. */
async function dressGlassveinDock(dock: THREE.Group, mats: RiverMats): Promise<void> {
  try {
    const hero = await loadGlb(DOCK_GLB);
    hero.name = 'RL-DOCK-HERO';
    const bark = mats.plank.map;
    const rock = mats.station.map;
    const wreck = mats.rust.map;
    const fern = mats.reed.map;
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (bark && (slug.includes('plank') || slug.includes('piling') || slug.includes('algae'))) {
          mat.map = slug.includes('algae') && fern ? fern : bark;
          mat.needsUpdate = true;
        }
        if (rock && slug.includes('concrete')) {
          mat.map = rock;
          mat.needsUpdate = true;
        }
        if (wreck && (slug.includes('rust') || slug.includes('roof') || slug.includes('enamel'))) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
      }
    });
    for (const child of [...dock.children]) {
      child.visible = false;
    }
    dock.add(hero);
  } catch {
    /* keep box dock */
  }
}

function addZodiac(root: THREE.Group, mats: RiverMats): THREE.Mesh {
  const pos = RL_NODES['RL-ZODIAC'] ?? { x: 80, y: 0.38, z: -7.55 };
  const hull = new THREE.Mesh(new THREE.CapsuleGeometry(0.55, 2.6, 4, 10), mats.hull);
  hull.rotation.z = Math.PI / 2;
  hull.position.set(pos.x, pos.y, pos.z);
  hull.castShadow = false;
  tag(hull, 'RL-ZODIAC');
  root.add(hull);

  const thwart = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.06, 0.28), mats.plank);
  thwart.position.set(pos.x, pos.y + 0.18, pos.z);
  root.add(thwart);
  return hull;
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}
